import { INVESTOR_SEARCH_ENDPOINT, InvestorTypeOptions } from './constants';

// Every selectable investor type, in option order. When the Investor Type
// filter is left on "Any" (no specific type picked), the request sends this
// full list rather than an empty array, so the search is explicit about asking
// for every type instead of relying on the service's own ANY default.
const ALL_INVESTOR_TYPES = InvestorTypeOptions.map((option) => option.value);

// Every investor-search helper: the request the search endpoint takes, the
// response it returns, the pins the map draws, the model the hover card reads,
// and the display formatters the cards use. Consolidated into one file so a
// contract change lands in one place.
//
// Endpoint: POST /ps-core/investors/search on the propstream-core gateway.
// Both directions are typed PropStream DTOs — the service owns the translation
// to and from BatchData, so no vendor field path leaks past this file.

export const SEARCH_PATH = INVESTOR_SEARCH_ENDPOINT;

// ---------------------------------------------------------------------------
// Request
// ---------------------------------------------------------------------------

// "123 Main St, Austin, TX 78701" -> parts. Used only when the resolved
// suggestion carried no structured fields.
const parseAddressLabel = (label) => {
  const parts = (label || '').split(',').map((s) => s.trim());
  const result = { street: parts[0] || '', city: parts[1] || '', state: '', zip: '' };

  if (parts.length >= 3) {
    const stateZip = parts[parts.length - 1];
    const match = stateZip.match(/^(.+?)\s+(\d{5}(-\d{4})?)$/);

    if (match) {
      result.state = match[1];
      result.zip = match[2];
    } else {
      result.state = stateZip;
    }
  }

  return result;
};

// Resolves a raw suggestion into the subject shape the rest of the surface
// works with. Lives here rather than in the SearchFilter component because the
// reducer needs it too, to preselect a lone suggestion.
export const suggestionToSubject = (suggestion) => {
  if (!suggestion) return null;

  const { type, cityId, id } = suggestion;
  const subject = { ...suggestion };

  if (type === 'P') subject.propertyId = id;
  else if (!cityId && type === 'C') subject.cityId = id;

  return subject;
};

const toSubject = (subject) => {
  const s = subject || {};
  const parsed = parseAddressLabel(s.label);

  return {
    street: s.streetAddress || s.street || parsed.street,
    city: s.city || parsed.city,
    state: s.state || parsed.state,
    zip: s.zip || parsed.zip || null,
  };
};

// Query string GET /ps-core/investors/subject-property-investor binds onto
// its SubjectAddress. A missing zip is left out rather than sent blank.
export const toSubjectQuery = (subject) => {
  const { zip, ...rest } = toSubject(subject);

  return zip ? { ...rest, zip } : rest;
};

// A blank filter dropdown means "no constraint", which the request expresses as
// an absent value — not an empty string, which fails the numeric validation.
const orNull = (value) => (value === '' || value === undefined ? null : value);

/**
 * The body POST /ps-core/investors/search accepts: the subject property, the
 * filter set and a sort mode.
 *
 * The service translates this into the vendor's buybox call itself — the
 * `searchCriteria`/`options` envelope, the classification equals/inList choice,
 * the property-type token and the result cap all live in its
 * BuyBoxRequestFactory. So nothing vendor-shaped belongs here; sending that
 * envelope instead is rejected with a 400, because it carries no `subject`.
 *
 * Every filter is optional: the service applies the search page's defaults
 * (radius 2, window 2, type ANY, sort BEST_MATCH) to anything left unset.
 */
export const toSearchRequest = (subject, filters) => {
  const f = filters && filters.toJS ? filters.toJS() : filters || {};

  return {
    subject: toSubject(subject),
    radiusMiles: orNull(f.radius),
    purchaseWindowYears: orNull(f.timeWindow),
    propertyType: orNull(f.propertyType),
    // "Any" (nothing picked) sends every type explicitly; a specific selection
    // sends just those.
    investorTypes: f.investorTypes && f.investorTypes.length ? f.investorTypes : ALL_INVESTOR_TYPES,
    avgPurchasePriceMin: orNull(f.avgPurchasePriceMin),
    avgPurchasePriceMax: orNull(f.avgPurchasePriceMax),
    minPropertiesPurchased: orNull(f.minPropertiesPurchased),
    sort: orNull(f.sort),
  };
};

// ---------------------------------------------------------------------------
// Response
// ---------------------------------------------------------------------------

// Each comparable now carries the full set of investors tied to it in
// `property.investors` — [{ id, isCurrentOwner, isPreviousOwner, isParent }] —
// rather than a single flat `investorId`/`currentOwner`. So the client resolves
// three situations from this shape:
//   1. an investor linked to several properties (grouped per investor below),
//   2. an investor that is a *previous* owner, not current (owner flag is read
//      per (investor, property) pair, so a past sale is never labelled owned),
//   3. a single property shared by several investors — we keep exactly one of
//      them (a current owner first, else the first listed), drop the rest, and
//      log the collision. An investor left with nothing to show after that is
//      removed entirely, so it gets neither a card nor a marker.
//
// Properties the investor still owns lead each investor's list, and
// `currentPropertyCount` lets the UI tell "still owns" from "has transacted
// here".
export const normalizeSearchResponse = (response) => {
  const payload = response || {};
  const investors = payload.investors || [];
  const properties = payload.comparableProperties || [];

  // investorId -> [{ property, currentOwner }] after single-investor resolution.
  const byInvestor = new Map();
  // Investor ids that were named on at least one property. An investor that
  // appeared somewhere but ended up with nothing is a collision loser and is
  // dropped; one that never appeared is left alone (behaviour preserved).
  const appeared = new Set();

  properties.forEach((property) => {
    const links = property.investors || [];
    links.forEach((link) => link && link.id && appeared.add(link.id));
    if (!links.length) return;

    // One investor per property: prefer a current owner, else the first listed.
    const chosen = links.find((link) => link && link.isCurrentOwner) || links[0];
    if (!chosen || !chosen.id) return;

    if (links.length > 1) {
      const dropped = links
        .filter((link) => link !== chosen && link && link.id)
        .map((link) => link.id);
      console.log(
        `[investors] property ${property.batchDataPropertyId || property.propertyId} ` +
          `(${property.addressLine || 'unknown address'}) has ${links.length} investors — ` +
          `keeping ${chosen.id}, filtering out ${dropped.join(', ')}`
      );
    }

    if (!byInvestor.has(chosen.id)) byInvestor.set(chosen.id, []);
    byInvestor.get(chosen.id).push({ property, currentOwner: Boolean(chosen.isCurrentOwner) });
  });

  const normalizedInvestors = investors
    .map((investor) => {
      const linked = byInvestor.get(investor.id) || [];
      const owned = linked.filter((link) => link.currentOwner);
      const rest = linked.filter((link) => !link.currentOwner);
      // Owned first; carry the resolved owner flag onto the property so pins and
      // the card can order/label without re-deriving it.
      const ordered = owned
        .concat(rest)
        .map((link) => ({ ...link.property, currentOwner: link.currentOwner }));

      return {
        ...investor,
        properties: ordered,
        propertyCount: ordered.length,
        currentPropertyCount: owned.length,
      };
    })
    // Drop investors that lost every property they were named on to a collision.
    .filter((investor) => investor.properties.length > 0 || !appeared.has(investor.id));

  return {
    // The count the UI shows reflects what it actually renders: investors
    // filtered out as shared-property collision losers are not counted.
    totalCount: normalizedInvestors.length,
    // The service caps the result set; surfaced so the count can be qualified.
    capped: Boolean(payload.capped),
    sort: payload.sort || null,
    subjectLocation: payload.subjectLocation || null,
    warnings: payload.warnings || [],
    properties,
    investors: normalizedInvestors,
  };
};

// ---------------------------------------------------------------------------
// Map pins
// ---------------------------------------------------------------------------

const position = (property) => {
  const lat = Number(property && property.latitude);
  const lng = Number(property && property.longitude);

  return Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0)
    ? { lat, lng }
    : null;
};

// One pin per investor, anchored on a property that investor owns — not one pin
// per comparable. `normalizeSearchResponse` has already resolved each property
// to a single investor, ordered that investor's properties currently-owned
// first, and removed investors that were filtered out by a shared-property
// collision, so a pin is simply anchored on this investor's first property we
// can both place (valid coordinates) and open (PropStream `propertyId`). An
// investor with no placeable property gets no pin. A property shared by several
// investors therefore yields a single marker — the one investor kept for it.
//
// Colour and glyph are still resolved at render time from the investor's first
// classification, so this stays free of presentation.
export const buildInvestorPins = (investors, subjectPropertyId) => {
  return (investors || [])
    .map((investor) => {
      // The property that anchors this investor's pin: their first resolved
      // comparable we can both place and open. No such property -> no marker.
      const linked = investor.properties || [];
      const property = linked.find((p) => position(p) && p.propertyId) || null;
      if (!property) return null;

      const [primaryType] = investor.classifications || [];

      // The searched subject renders as the star pin (Figma 231:8427). Matched on
      // PropStream's `propertyId` rather than comparing address fields one by one:
      // the same propertyId IS the same property, so every location value (street,
      // city, state, zip) matches by definition — and the comparables don't carry
      // those fields to compare anyway. Only flags a pin when the subject property
      // is itself one an investor owns, so it appears among the markers.
      const isSubject = Boolean(subjectPropertyId) && property.propertyId === subjectPropertyId;

      return {
        key: investor.id,
        position: position(property),
        property,
        investor,
        investorId: investor.id,
        investorName: investor.name || null,
        type: primaryType || null,
        isSubject,
      };
    })
    .filter(Boolean);
};

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

const EMPTY = '—';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const isMissing = (value) =>
  value === null || value === undefined || !Number.isFinite(Number(value));

const plural = (count, word) => (count === 1 ? word : `${word}s`);

export const formatCount = (value) =>
  isMissing(value) ? EMPTY : Number(value).toLocaleString('en-US');

export const formatProperties = (value) => {
  if (isMissing(value)) return EMPTY;
  const count = Number(value);

  return `${count.toLocaleString('en-US')} ${count === 1 ? 'Property' : 'Properties'}`;
};

export const formatPurchases = (value) => {
  if (isMissing(value)) return EMPTY;
  const count = Number(value);

  return `${count.toLocaleString('en-US')} ${plural(count, 'Purchase')}`;
};

export const formatCompactCurrency = (value) => {
  if (isMissing(value)) return EMPTY;

  const amount = Math.round(Number(value));
  if (amount >= 1000000) {
    const millions = amount / 1000000;

    return `$${(millions >= 10 ? Math.round(millions) : Math.round(millions * 10) / 10).toLocaleString('en-US')}M`;
  }
  if (amount >= 1000) return `$${Math.round(amount / 1000).toLocaleString('en-US')}K`;

  return `$${amount.toLocaleString('en-US')}`;
};

export const formatPrice = (value) => {
  if (isMissing(value) || Number(value) === 0) return null;

  return `$${Math.round(Number(value)).toLocaleString('en-US')}`;
};

export const formatOwnership = (months) => {
  if (isMissing(months) || Number(months) <= 0) return EMPTY;

  const total = Math.round(Number(months));
  if (total < 24) return `${total} ${plural(total, 'month')} avg`;

  const years = Math.round((total / 12) * 10) / 10;

  return `${years} ${years === 1 ? 'year' : 'years'} avg`;
};

// matchScore is a 0-1 fraction.
export const formatMatchScore = (score) => {
  if (isMissing(score)) return null;

  return `${Math.round(Number(score) * 100)}% Match`;
};

// Plain-language description behind the match pill's hover — Figma 3:262. Only
// the description sentence is built (no "Why this match?" title, strategy line
// or pills, per the ticket). Each clause is dropped when its metric is missing
// or non-positive, so a sparse investor still reads as a clean sentence instead
// of "$0" / "0 properties". Values reuse the same formatters the card's metrics
// use, so the numbers match what's shown elsewhere on the card.
export const formatInvestorMatchSummary = (investor) => {
  const inv = investor || {};
  const metrics = inv.metrics || {};
  const parts = [];

  const portfolio = Number(metrics.portfolioSize);
  const entityType = (inv.entityType || '').trim();
  if (Number.isFinite(portfolio) && portfolio > 0) {
    const noun = portfolio === 1 ? 'property' : 'properties';
    const count = portfolio.toLocaleString('en-US');
    parts.push(entityType ? `${entityType} with ${count} ${noun}.` : `Owns ${count} ${noun}.`);
  } else if (entityType) {
    parts.push(`${entityType}.`);
  }

  const acquired = Number(metrics.acquisitionCountLast12Months);
  if (Number.isFinite(acquired) && acquired > 0) {
    const noun = acquired === 1 ? 'property' : 'properties';
    parts.push(`Purchased ${acquired.toLocaleString('en-US')} ${noun} in the last 12 months.`);
  }

  const avgPrice = Number(metrics.avgPurchasePrice);
  if (Number.isFinite(avgPrice) && avgPrice > 0) {
    parts.push(`Average purchase price: ${formatCompactCurrency(avgPrice)}.`);
  }

  return parts.join(' ');
};

// The search response sends `lastSaleDate` as epoch milliseconds in a string
// ("1738324800000"), which `new Date` reads as an invalid date string — so a
// digits-only value is turned into a number first. ISO dates still parse as-is.
export const formatPurchasedOn = (value) => {
  if (!value) return null;

  const date = new Date(/^\d+$/.test(String(value)) ? Number(value) : value);
  if (Number.isNaN(date.getTime())) return null;

  return `(purchased on ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()})`;
};

// "3 bed | 3 baths | 1,653 sqft", omitting whatever the service did not return.
export const formatPropertyFacts = (property) => {
  const p = property || {};
  const parts = [];
  const beds = Number(p.bedrooms);
  const baths = Number(p.bathrooms);
  const sqft = Number(p.livingAreaSquareFeet);

  if (beds > 0) parts.push(`${beds} bed`);
  if (baths > 0) parts.push(`${baths} ${plural(baths, 'bath')}`);
  if (sqft > 0) parts.push(`${sqft.toLocaleString('en-US')} sqft`);

  return parts.join('  |  ');
};

// The service returns a single formatted line.
export const formatPropertyAddress = (property) => (property && property.addressLine) || '';

// ---------------------------------------------------------------------------
// Hover-card model
// ---------------------------------------------------------------------------

const MAX_PHONES = 3;
const MAX_EMAILS = 2;

// Contacts arrive as people, each with phones and emails; the card shows one
// comma-separated run of each. Duplicates across people are collapsed, and
// DNC-flagged numbers are dropped — surfacing a number a user must not call is
// worse than showing one fewer.
export const collectPhones = (contacts) => {
  const seen = [];

  (contacts || []).forEach((contact) =>
    (contact.phones || []).forEach((phone) => {
      if (!phone || !phone.number || phone.dnc === true) return;
      if (seen.indexOf(phone.number) === -1) seen.push(phone.number);
    })
  );

  return seen.slice(0, MAX_PHONES);
};

export const collectEmails = (contacts) => {
  const seen = [];

  (contacts || []).forEach((contact) =>
    (contact.emails || []).forEach((entry) => {
      const email = entry && (entry.email || entry);
      if (!email || typeof email !== 'string') return;
      if (seen.indexOf(email) === -1) seen.push(email);
    })
  );

  return seen.slice(0, MAX_EMAILS);
};

export const formatPhone = (number) => {
  const digits = String(number || '').replace(/\D/g, '');
  if (digits.length !== 10) return number;

  return `(${digits.slice(0, 3)})-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

// ---------------------------------------------------------------------------
// Materialize (Add to Investor List / Skip Trace payload)
// ---------------------------------------------------------------------------

// BatchData's entityType vocabulary isn't pinned down in the design docs;
// treat anything that reads as a business entity as a company, else an
// individual whose display name splits into first/last on the first space.
const isCompanyEntity = (entityType) =>
  /company|business|llc|corp|entity|trust/i.test(entityType || '');

const splitPersonName = (name) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  return { firstName: parts[0] || '', lastName: parts.slice(1).join(' ') };
};

// Same dedupe-by-number idea as collectPhones, but keeps the per-number `dnc`
// flag instead of dropping DNC'd numbers — the materialize payload must
// persist it (contact_phones.dnc), not just hide it from display.
const collectRawPhones = (contacts) => {
  const seen = new Map();

  (contacts || []).forEach((contact) =>
    (contact.phones || []).forEach((phone) => {
      if (!phone || !phone.number) return;
      if (!seen.has(phone.number)) seen.set(phone.number, Boolean(phone.dnc));
    })
  );

  return Array.from(seen, ([number, dnc]) => ({ number, dnc }));
};

/**
 * Builds the propstream-be `InvestorContactRequest` shape (ATAI-116, PR #1662)
 * from one investor search result — the payload the Add-to-Investor-List (and,
 * later, Skip-Trace) flows materialize through.
 */
export const toInvestorContactRequest = (investor) => {
  const inv = investor || {};
  const identity = isCompanyEntity(inv.entityType)
    ? { firstName: '', lastName: '', companyName: inv.name || '' }
    : { ...splitPersonName(inv.name), companyName: '' };
  const primaryAddress = (inv.mailingAddresses || [])[0] || {};

  return {
    batchdataInvestorId: inv.id,
    ...identity,
    mailStreetAddress: primaryAddress.street || '',
    mailCity: primaryAddress.city || '',
    mailState: primaryAddress.state || '',
    mailZip: primaryAddress.zip || '',
    phones: collectRawPhones(inv.contacts),
    emails: collectEmails(inv.contacts),
  };
};

// GET subject-property-investor -> { investor, property }. `investor` is the
// owner in the same shape as a search result — entity type, metrics, mailing
// addresses, the caller's saved lists, and contacts gated to the caller's own
// skip-traced ones exactly as search gates them — or null when no investor owns
// the property. `property` is a comparable. Both pass through as-is, so the
// investor card, Save and Skip Trace treat the owner like any search result.
export const normalizeSubjectProperty = (response) => {
  const payload = response || {};
  const property = payload.property || null;
  if (!property) return null;

  return { investor: payload.investor || null, property };
};

// The standalone subject star pin as an investor pin, so it opens the same
// investor card (InvestorInfoPopover) on click — with the investor the subject
// property lookup itself returned, not one from the search results. Null when
// the lookup found no owning investor; the star then stays a plain marker.
export const buildSubjectPin = (subjectProperty, at) => {
  const { investor, property } = subjectProperty || {};
  if (!investor || !investor.id || !at) return null;

  const [primaryType] = investor.classifications || [];

  return {
    key: `subject:${investor.id}`,
    position: at,
    property,
    investor,
    investorId: investor.id,
    investorName: investor.name || null,
    type: primaryType || null,
    isSubject: true,
  };
};

export const buildPopoverModel = (pin) => {
  const investor = (pin && pin.investor) || {};
  const property = (pin && pin.property) || {};

  return {
    name: investor.name || 'Unknown investor',
    entityType: investor.entityType || null,
    classifications: investor.classifications || [],
    matchScore: formatMatchScore(investor.matchScore),
    phones: collectPhones(investor.contacts),
    emails: collectEmails(investor.contacts),
    price: formatPrice(property.lastSalePrice),
    purchasedOn: formatPurchasedOn(property.lastSaleDate),
    address: formatPropertyAddress(property),
    // Raw URL, not a resolved photo: choosing the no-photo tile is a
    // presentation decision and its asset belongs in app/, not the data layer.
    // app/InvestorOutreach/propertyPhoto reads `imageUrl` off this model.
    imageUrl: property.imageUrl || null,
    beds: Number.isFinite(Number(property.bedrooms)) ? Number(property.bedrooms) : null,
    baths: Number.isFinite(Number(property.bathrooms)) ? Number(property.bathrooms) : null,
    squareFeet: Number.isFinite(Number(property.livingAreaSquareFeet))
      ? Number(property.livingAreaSquareFeet)
      : null,
    lot: Number.isFinite(Number(property.lotSquareFeet)) ? Number(property.lotSquareFeet) : null,
    ownedCount: investor.propertyCount || 0,
  };
};
